/**
 * PointerInputFields — two editable HTML inputs that float near the cursor
 * during an active draft session (AutoCAD Dynamic Input, pointer mode).
 *
 * Layout mirrors AutoCAD:
 *  - Polar mode:     [distance] < [angle°]
 *  - Cartesian mode: [X] , [Y]
 *
 * The component is fully controlled: all state lives in the parent
 * (DynamicInputOverlay) and flows down as props. The component only fires
 * `onEvent` callbacks upward.
 *
 * Display logic (DYNPIVIS parity):
 *  - state.typing === false: render read-only tooltip with live values
 *    (shown only when DYNPIVIS=2, or as placeholder text when DYNPIVIS=1).
 *    The *actual* toggle between read-only and editable is done by the
 *    parent via the `visible` prop; this component renders whatever it
 *    receives.
 *  - state.typing === true: focused field shows buffer; other field shows
 *    lock value if locked, else live value as placeholder.
 *
 * Keyboard contract:
 *  Tab         → onEvent({type:"tab"})
 *  Shift+Tab   → onEvent({type:"shift-tab"})
 *  Enter       → onSubmit()
 *  Esc         → onEscape()
 *  any input   → onEvent({type:"change-buffer", key, value})
 */

import * as React from "react";
import { useEffect, useRef } from "react";

import { useT } from "../i18n";
import type { ScreenPoint } from "./fieldGeometry";
import type { CoordsMode, FieldEvent, FieldKey, FieldState, InputFormat } from "./fieldBehavior";

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  /** When false the component renders nothing. */
  visible: boolean;
  /** CSS-pixel position of the fields container. */
  screenPosition: ScreenPoint;
  /** Current field machine state. */
  state: FieldState;
  /** Whether fields show distance/angle (polar) or X/Y (cartesian). */
  mode: CoordsMode;
  /** Whether coordinates are relative or absolute. */
  format: InputFormat;
  /**
   * Live cursor-derived values displayed as placeholders when the
   * corresponding field is not typed into.
   */
  liveValues: { primary: string; secondary: string };
  /** Dispatch a field event upward to the reducer in DynamicInputOverlay. */
  onEvent: (event: FieldEvent) => void;
  /** Called when Enter is pressed — parent should build token and commit. */
  onSubmit: () => void;
  /** Called when Esc is pressed — parent should cancel draft or clear state. */
  onEscape: () => void;
};

// ─── Component ───────────────────────────────────────────────────────────────

export function PointerInputFields(props: Props): React.ReactElement | null {
  const t = useT();
  const primaryRef = useRef<HTMLInputElement>(null);
  const secondaryRef = useRef<HTMLInputElement>(null);

  const { visible, state, mode, liveValues, onEvent, onSubmit, onEscape } = props;

  // Auto-focus the correct input whenever `focused` changes.
  useEffect(() => {
    if (!visible || !state.typing) return;
    if (state.focused === "primary") {
      primaryRef.current?.focus();
    } else if (state.focused === "secondary") {
      secondaryRef.current?.focus();
    }
  }, [visible, state.typing, state.focused]);

  if (!visible) return null;

  // Labels depend on mode.
  const primaryLabel =
    mode === "polar" ? t("dynin.field.distance") : t("dynin.field.x");
  const secondaryLabel =
    mode === "polar" ? t("dynin.field.angle") : t("dynin.field.y");

  // The separator character between fields mirrors AutoCAD:
  //   polar → "<"   (distance < angle)
  //   cartesian → ","
  const separator = mode === "polar" ? "<" : ",";

  // ── Key handler (shared between both inputs) ──────────────────────────────

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>, key: FieldKey) {
    if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) {
        onEvent({ type: "shift-tab" });
      } else {
        onEvent({ type: "tab" });
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onEscape();
      return;
    }
    // Let all other keys fall through to the input's default handling.
    void key;
  }

  // ── Field value logic ─────────────────────────────────────────────────────

  /**
   * Returns the value to display in an input.
   * - Locked field: shows the locked numeric string (read-only).
   * - Focused typing field: shows the raw buffer (editable).
   * - Unfocused / not-locked field: shows live value as placeholder (editable
   *   but placeholder attribute, value="").
   */
  function getFieldValue(key: FieldKey): string {
    if (state.locks[key] !== null) {
      return String(state.locks[key]);
    }
    if (state.typing) {
      return state.buffer[key];
    }
    return "";
  }

  function getPlaceholder(key: FieldKey): string {
    if (state.locks[key] !== null) return "";
    return liveValues[key];
  }

  function isReadOnly(key: FieldKey): boolean {
    return state.locks[key] !== null;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="dynin-fields-container"
      style={{
        left: props.screenPosition.leftPx,
        top: props.screenPosition.topPx,
      }}
      data-testid="dynin-fields"
    >
      {/* Primary field label */}
      <span className="dynin-field-label">{primaryLabel}</span>

      {/* Primary input */}
      <input
        ref={primaryRef}
        type="text"
        inputMode="decimal"
        className="dynin-field"
        data-testid="dynin-field-primary"
        data-focused={state.focused === "primary" ? "true" : "false"}
        data-locked={isReadOnly("primary") ? "true" : "false"}
        data-invalid={state.invalid.primary ? "true" : "false"}
        readOnly={isReadOnly("primary")}
        value={getFieldValue("primary")}
        placeholder={getPlaceholder("primary")}
        aria-label={primaryLabel}
        onKeyDown={(e) => handleKeyDown(e, "primary")}
        onChange={(e) => {
          if (!isReadOnly("primary")) {
            onEvent({ type: "change-buffer", key: "primary", value: e.target.value });
          }
        }}
        onFocus={() => {
          // No event dispatched here — focus is managed via the reducer.
        }}
      />

      {/* Separator */}
      <span className="dynin-separator" aria-hidden="true">
        {separator}
      </span>

      {/* Secondary field label */}
      <span className="dynin-field-label">{secondaryLabel}</span>

      {/* Secondary input */}
      <input
        ref={secondaryRef}
        type="text"
        inputMode="decimal"
        className="dynin-field"
        data-testid="dynin-field-secondary"
        data-focused={state.focused === "secondary" ? "true" : "false"}
        data-locked={isReadOnly("secondary") ? "true" : "false"}
        data-invalid={state.invalid.secondary ? "true" : "false"}
        readOnly={isReadOnly("secondary")}
        value={getFieldValue("secondary")}
        placeholder={getPlaceholder("secondary")}
        aria-label={secondaryLabel}
        onKeyDown={(e) => handleKeyDown(e, "secondary")}
        onChange={(e) => {
          if (!isReadOnly("secondary")) {
            onEvent({ type: "change-buffer", key: "secondary", value: e.target.value });
          }
        }}
        onFocus={() => {
          // No event dispatched here.
        }}
      />
    </div>
  );
}
